import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { useUi } from '../stores/ui.js';
import { refreshCookies } from '../api/carrefour.js';
import { errorMessage } from '../lib/errors.js';

export function CarrefourCookiesModal(): React.ReactElement | null {
  const open = useUi((s) => s.carrefourCookiesOpen);
  const close = useUi((s) => s.closeCarrefourCookies);

  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  if (!open) return null;

  async function save(): Promise<void> {
    if (submitting) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const r = await refreshCookies(value);
      setSuccess(`${r.cookieCount} cookies enregistrés`);
      setValue('');
      setTimeout(() => close(), 1200);
    } catch (e) {
      setError(errorMessage(e, 'Erreur inconnue'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Refresh Carrefour cookies"
      closeLabel="Fermer"
      initialFocusRef={textareaRef}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Annuler
          </Button>
          <Button onClick={save} disabled={!value.trim()} loading={submitting}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="pile">
        <ol className="discret pl-5 list-decimal space-y-1">
          <li>
            Ouvre <span className="text-fg">carrefour.fr</span> dans Chrome (connecté à ton compte)
          </li>
          <li>
            Clic sur l'extension <span className="text-fg">Cookie-Editor</span> → Export → JSON
          </li>
          <li>Colle le JSON ci-dessous</li>
        </ol>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder='[{"domain":".carrefour.fr",...}]'
          rows={8}
          aria-label="Cookies au format JSON"
          className="saisie font-mono"
        />

        <p className="champ-erreur" role="alert">
          {error}
        </p>
        {success && (
          <p className="alerte alerte-succes alerte-sobre" role="status">
            {success}
          </p>
        )}
      </div>
    </Modal>
  );
}
